import React, { useEffect, useState } from 'react'
import { connect as connectRedux } from 'react-redux'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { bindActionCreators } from 'redux'
import { compose } from 'recompose'
import { Map } from 'immutable'
import _pick from 'lodash/pick'
import connectHistory from 'gui/components/router/connect'
import ErrorMessage from 'gui/components/error-message'
import { Container, Grid, Snackbar, Alert, Link, Typography } from '@mui/material'
import MuiForm from 'gui/components/common/MuiForm'
import MuiTextFieldWrapper from 'gui/components/common/MuiTextField'
import MuiButton from 'gui/components/common/MuiButton'
import LanguageSelect from 'gui/components/language-select'
import ContentTypeSelect from 'gui/components/content-type-select'
import ContentDisplayTypeSelect from 'gui/components/content-display-type-select'
import ContentProductTypeSelect from 'gui/components/content-product-type-select'
import Loader from 'gui/components/common/Loader'
import { findView, getViewRecordsAsList, getApplicationDataByKey } from 'services/data'
import {
  persistData as persistDataAction,
  setDataList as setDataListAction,
  setDataApplicationByKey as setDataApplicationByKeyAction,
} from 'services/data/redux/actions'
import {
  CONTENT_CREATE_APPLICATION_KEY,
  CONTENT_PATH_STORE_KEY,
  CONTENT_STORE_KEY,
  createContentCreateViewOptions,
  createContentPathCreateViewOptions,
} from 'services/content'
import {
  CONTENT_TYPE_BONUS_PACKAGE,
} from 'services/content/content-types'
import './index.scss'

/**
 * Handle onSubmit event
 * @param {Event} e The submit event
 * @param {Object} props The react props
 * @param {Function} props.persistData Persist data tos the api
 * @param {String} props.storeKey The store key
 * @param {String} props.applicationKey The application key
 * @param {Map} props.location The redux location state
 * @param {Map} props.applicationData The current application data
 */
function onSubmit (e, props) {
  const { applicationKey, storeKey, persistData, location, applicationData } = props
  e.preventDefault()
  const isValid = e.target.checkValidity()
  if (isValid) {
    // Disable language translation by forcing to be false
    const createTranslationJob = false
    persistData({
      storeKey,
      applicationKey,
      processing: true,
      options: createContentCreateViewOptions({ location }, { createTranslationJob }),
      record: applicationData.set('createTranslationJob', createTranslationJob),
    })
  }
}
/**
 * Handle onChange event
 * @param {Event} e The event
 * @param {Object} props The props
 * @param {String} props.storeKey The store key
 * @param {String} props.applicationKey The application key
 * @param {Map} props.applicationData The current application data
 * @param {typeof setDataApplicationByKeyAction} props.setDataApplicationByKey
 *   Persist data to the api
 */
function onChange (e, props) {
  const { storeKey, applicationKey, setDataApplicationByKey, applicationData } = props
  const {
    target: { checked, name, type, value },
  } = e
  setDataApplicationByKey({
    storeKey,
    applicationKey,
    applicationData: applicationData.set(name, type === 'checkbox' ? checked : value),
  })
}

/**
 * Get the state for the component
 * @param {Object} props The react props
 * @param {Map} props.data The redux data state
 * @param {Map} props.location The redux location state
 * @param {String} props.storeKey The store key
 * @param {String} props.storeKeyContentPath The content path store key
 * @param {String} props.applicationKey The application key
 * @returns {Boolean} The processing and error values
 */
function getState (props) {
  const { data, storeKey, storeKeyContentPath, applicationKey, location } = props
  const options = createContentCreateViewOptions({ location }, { createTranslationJob: false })

  const view =
    findView({
      data,
      storeKey,
      options,
    }) || Map()
  const applicationData =
    getApplicationDataByKey({
      data,
      storeKey,
      applicationKey,
    }) || Map()
  const records =
    getViewRecordsAsList({
      data,
      storeKey,
      options,
    }) || Map()

  const optionsContentPath = createContentPathCreateViewOptions({ content: applicationData })
  const viewContentPath =
    findView({
      data,
      storeKey: storeKeyContentPath,
      options: optionsContentPath,
    }) || Map()
  const recordContentPath =
    getViewRecordsAsList({
      data,
      storeKey: storeKeyContentPath,
      options: optionsContentPath,
    }).first() || Map()

  // Try to find record by application language first
  let foundRecord =
    applicationData.get('language') &&
    records.find((v) => v.get('language') === applicationData.get('language'))

  // If no record found by language, but we have records and we're not processing,
  // take the first record (this handles the case where app data was cleared)
  if (!foundRecord && records.size > 0 && !view.get('processing')) {
    foundRecord = records.first()
  }

  return {
    processing: view.get('processing'),
    processingContentPath: viewContentPath.get('processing'),
    error: view.get('error'),
    errorContentPath: viewContentPath.get('error'),
    records,
    // This is the created record
    record: foundRecord,
    options,
    applicationData,
    recordContentPath,
  }
}

/**
 * Component which renders a loader for content create
 */
function ContentCreateLoader () {
  return (
    <div className="loader-container">
      <Loader />
      <div className="loader__update-mask" />
    </div>
  )
}

/**
 * A component to create content
 * @param {Object} props The react props
 * @param {History} props.history The history singleton
 * @param {Map} props.data The redux data state
 * @param {Map} props.location The redux location state
 * @param {String} props.storeKey The store key
 * @param {String} props.storeKeyContentPath The content path store key
 * @param {String} props.applicationKey The application key
 * @param {typeof persistDataAction} props.persistData Persist data to the api
 * @param {typeof setDataApplicationByKeyAction} props.setDataApplicationByKey
 *   Set the application data
 */
function ContentCreate (props) {
  const { storeKey, setDataList } = props
  const [snackbar, setSnackbar] = useState(null)
  const {
    processing,
    processingContentPath,
    error,
    errorContentPath,
    record,
    options,
    applicationData,
    recordContentPath,
    records,
  } = getState(props)
  const translatedLanguages = records.map((v) => v.get('language'))
  const translation = options.get('translation')

  const displaySnackbar = (message, severity = 'info', persist = false, contentId = null) => {
    const snackbarOptions = {
      message,
      severity,
      duration: persist ? null : 10000, // 10 seconds default
      contentId,
    }
    setSnackbar(snackbarOptions)
  }

  const handleSnackbarClose = () => setSnackbar(null)

  useEffect(() => {
    if (record) {
      const nodeId = record.get('id')
      const contentId = record.get('contentId')

      // Show success snackbar with the created content ID and link
      displaySnackbar(
        `Content ${contentId} has been created. Click to view.`,
        'success',
        false,
        nodeId,
      )

      // setting the data list records to an empty list so it clears out
      // and have ability to create a new content
      setDataList({
        storeKey,
        options,
        records: Map(),
        total: 0,
        processing: false,
      })
    }
  }, [record, storeKey, options, setDataList])
  // If we are processing and the application data is not yet loaded
  // we are loading translation records
  if (processing && applicationData.isEmpty()) {
    return <ContentCreateLoader />
  }
  return (
    <Container className="content-create">
      <div className="content-create__form-wrapper">
        <Typography variant="h4" component="h1" gutterBottom sx={{ marginBottom: 3 }}>
          Content Creation
        </Typography>
        <MuiForm
          className="content-create__form"
          onSubmit={(e) => onSubmit(e, { ...props, applicationData })}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <LanguageSelect
                id="language"
                onChange={(e) => onChange(e, { ...props, applicationData })}
                value={applicationData.get('language') || ''}
                className="content-create__input"
                label="Primary Language"
                excludeLanguages={translatedLanguages.isEmpty() ? undefined : translatedLanguages}
                required
                noMargin
              />
            </Grid>
            <Grid item xs={12}>
              <ContentTypeSelect
                id="contentType"
                onChange={(e) => onChange(e, { ...props, applicationData })}
                value={applicationData.get('contentType') || ''}
                className="content-create__input"
                label="Content type"
                disabled={translation}
                required
                excludeTypes={[CONTENT_TYPE_BONUS_PACKAGE]}
                noMargin
              />
            </Grid>
            <Grid item xs={12}>
              <MuiTextFieldWrapper
                id="title"
                onChange={(e) => onChange(e, { ...props, applicationData })}
                value={applicationData.get('title') || ''}
                className="content-create__input"
                maxLength="250"
                minLength="1"
                label="Title"
                name="title"
                type="text"
                required
                noMargin
              />
            </Grid>
            <Grid item xs={12}>
              <MuiTextFieldWrapper
                id="path"
                // Don't listen to onChange since this is set by title
                onChange={() => {}}
                value={applicationData.get('path') || recordContentPath.get('path') || ''}
                className="content-create__input"
                label="Path"
                name="path"
                type="text"
                // Disabled since this is set by title
                disabled
                noMargin
              />
            </Grid>
            <Grid item xs={12}>
              <ContentDisplayTypeSelect
                id="displayType"
                onChange={(e) => onChange(e, { ...props, applicationData })}
                contentType={applicationData.get('contentType')}
                className="content-detail__input"
                label="Display type"
                name="displayType"
                value={applicationData.get('displayType')}
                required
                disabled={translation}
                noMargin
              />
            </Grid>
            <Grid item xs={12}>
              <ContentProductTypeSelect
                id="productType"
                onChange={(e) => onChange(e, { ...props, applicationData })}
                className="content-detail__input"
                label="Product Type"
                name="productType"
                contentType={applicationData.get('contentType')}
                value={applicationData.get('productType') || ''}
                required
                disabled={translation}
                noMargin
              />
            </Grid>
            <Grid item xs={12}>
              <MuiButton
                type="submit"
                disabled={processing || processingContentPath}
                className="content-create__submit"
              >
                {processing ? 'processing' : 'submit'}
              </MuiButton>
            </Grid>
          </Grid>
          {error && <ErrorMessage error={error} />}
          {errorContentPath && <ErrorMessage error={errorContentPath} />}
        </MuiForm>
      </div>
      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={handleSnackbarClose}
          autoHideDuration={snackbar.duration}
        >
          <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
            {snackbar.contentId ? (
              <Link
                href={`/content/${snackbar.contentId}`}
                color="inherit"
                underline="hover"
                sx={{ cursor: 'pointer' }}
              >
                {snackbar.message}
              </Link>
            ) : (
              snackbar.message
            )}
          </Alert>
        </Snackbar>
      )}
    </Container>
  )
}

ContentCreate.propTypes = {
  data: ImmutablePropTypes.map.isRequired,
  history: PropTypes.object.isRequired,
  location: ImmutablePropTypes.map.isRequired,
  persistData: PropTypes.func.isRequired,
  storeKey: PropTypes.string.isRequired,
  storeKeyContentPath: PropTypes.string.isRequired,
  setDataList: PropTypes.func.isRequired,
  setDataApplicationByKey: PropTypes.func.isRequired,
}

ContentCreate.defaultProps = {
  storeKey: CONTENT_STORE_KEY,
  storeKeyContentPath: CONTENT_PATH_STORE_KEY,
  applicationKey: CONTENT_CREATE_APPLICATION_KEY,
}

export default compose(
  connectRedux(
    (state) => _pick(state, ['data', 'location']),
    (dispatch) =>
      bindActionCreators(
        {
          persistData: persistDataAction,
          setDataList: setDataListAction,
          setDataApplicationByKey: setDataApplicationByKeyAction,
        },
        dispatch,
      ),
  ),
  connectHistory(),
)(ContentCreate)
